import { Component, Input, OnInit, EventEmitter, Output, OnDestroy, AfterViewInit, Inject } from '@angular/core';
import { XsI18nPipe } from '@nx-fe/pipes';
import { FeatureToggleService, hd } from '@nx-fe/services';
import { FileDownloadService } from 'app/modules/remote-control-ng/services/downloadService';
import { RcDeviceService } from 'app/modules/remote-control/services/rc-device-service';
import { DeviceCheckinStateService } from 'app/modules/signalr/broadcasters/checkin.state.service';
import { SnapshotService } from 'app/modules/ticket/services/snapshot.service';
import { Toast, ToastTypeIndex } from 'app/shared/components/toast/toast.component';
import { Subscription, SubscriptionLike } from 'rxjs';
import { first } from 'rxjs/operators';

interface IButtonInfo {
  icon: string;
  toolTipName: () => string;
  cssClass?: string;
  onClick?: () => void;
  isDisabled?: () => boolean;
  isSpinning?: () => boolean;
  isVisible?: () => boolean;
}

@Component({
  selector: 'xs-snapshot-buttons',
  templateUrl: './snapshot-buttons.component.html',
  styleUrls: ['./snapshot-buttons.component.scss'],
})
export class SnapshotButtonsComponent implements OnInit, OnDestroy, AfterViewInit {
  private _device: hd.IDeviceModel;
  private deviceCanCheckin = false;

  @Input() isLicenseActive: boolean = true;
  @Input() set device(value: hd.IDeviceModel) {
    this._device = value;
    this.updateCheckinStatus();
    this.initCheckinStatus();
  }

  get device(): hd.IDeviceModel {
    return this._device;
  }

  get deviceId(): string {
    if (this.device == null) {
      return null;
    }
    return this._device.deviceId;
  }
  @Input() disableRefresh: boolean;
  // remove when MergeRefreshCheckin get enabled
  @Input() disableCheckin: boolean;
  @Input() isSaveSnapshotEnabled: boolean;

  @Output() refreshPressed = new EventEmitter<void>();
  @Output() checkInPressed = new EventEmitter<void>();
  @Output() isAnyButton = new EventEmitter<boolean>();
  @Output() setToast = new EventEmitter<Toast>();

  deviceCheckinInProgressSub: SubscriptionLike;
  private isFetching = false;
  private fetchingStatusSub: Subscription;
  private refreshing = false;

  private getRefreshTooltip = () => {
    if (this.isLicenseActive) {
      return this.saI18nPipe.transform('remoteControl_btn_Reload_Snapshot')
    } else {
      return this.saI18nPipe.transform('deviceInfo_Refresh_DeviceInfo')
    }
  }

  private ButtonList: IButtonInfo[] = [
    {
      icon: 'hd-icon-refresh',
      toolTipName: this.getRefreshTooltip,
      onClick: () => {
        if (this.refreshPressed && !this.disableRefresh && !this.refreshing) {
          this.refreshPressed.emit();
        }
        else {
          this.setToast.emit({
            message: this.saI18nPipe.transform('snapshot_ErrorRefreshSnapshot'),
            type: ToastTypeIndex.error,
          });
        }
      },
      isDisabled: () => this.disableRefresh || this.isFetching,
      isSpinning: () => this.disableRefresh,
      isVisible: () => this.canCheckin(),
    },
  ];

  private updateCheckinStatus = () => {
    const deviceId = this.deviceId;
    if (deviceId !== null) {
      this.disableCheckin = this.deviceCheckinState.checkinStatus[this.deviceId] !== undefined;
    }
  };

  constructor(
    private saI18nPipe: XsI18nPipe,
    private deviceCheckinState: DeviceCheckinStateService,
    private featureToggleService: FeatureToggleService,
    private snapshotService: SnapshotService,
    private deviceService: RcDeviceService,
    private downloadService: FileDownloadService
  ) {}
  ngAfterViewInit() {
    this.fetchingStatusSub = this.snapshotService.fetchingStatus$.subscribe(isFetching => {
      setTimeout(() => {
        if (this.isFetching && !isFetching) {
          // finishing fetching
          this.resetRefresh();
        }
        this.isFetching = isFetching;
      }, 0);
    });
  }

  ngOnInit() {
    this.ButtonList.push({
      icon: 'hd-icon-savedeviceinfo',
      toolTipName: () => this.saI18nPipe.transform('remoteControl_btn_Save_Snapshot'),
      onClick: () => {
        this.snapshotService.requestSaveDeviceSnapshot(this.deviceId);
      },
      isDisabled: () => this.isSaveSnapshotDisabled(),
      isVisible: () => this.isSaveSnapshotEnabled,
    });

    this.deviceCheckinInProgressSub = this.deviceCheckinState.deviceCheckinInProgress$.subscribe((value: any) => {
      this.updateCheckinStatus();
    });

    if (this.featureToggleService.isEnabled('ExportSnapshotPdf')) {
      this.ButtonList.push({
        icon: 'hd-icon-filetype-pdf',
        toolTipName: () => this.saI18nPipe.transform('remoteControl_btn_Pdf_Snapshot'),
        onClick: () => {
          const activityId = this.snapshotService.getSnapshotMetaData(this.deviceId).activityId;
          this.snapshotService.exportSnapshot(activityId).subscribe(
            (pdfData: Blob) => {
              if (pdfData !== undefined) {
                const blob = new Blob([pdfData], { type: pdfData.type });
                const deviceSnapshotFileName = 'DeviceSnapshot-' + new Date().toLocaleDateString() + '.pdf';
                this.downloadService.triggerDownload(blob, deviceSnapshotFileName);
              }
            },
            error => {
              console.error('Failed to generate Device Snapshot pdf of activityId: ', activityId);
              console.error(error);
            }
          );
        },
        isDisabled: () => this.isSaveSnapshotDisabled(),
        isVisible: () => this.isSaveSnapshotEnabled,
      });
    }

    this.isAnyButton.emit(this.ButtonList.some(button => button.isVisible()));
  }

  ngOnDestroy(): void {
    if (this.deviceCheckinInProgressSub) {
      this.deviceCheckinInProgressSub.unsubscribe();
    }

    if (this.fetchingStatusSub) {
      this.fetchingStatusSub.unsubscribe();
    }
  }

  public getButtonList = (): IButtonInfo[] => this.ButtonList;

  public click(button: IButtonInfo) {
    if (button.onClick && !this.isDisabled(button)) {
      button.onClick();
    }
  }

  public isSpinning(button: IButtonInfo) {
    return button.isSpinning && button.isSpinning();
  }

  public isDisabled(button: IButtonInfo) {
    return button.isDisabled && button.isDisabled();
  }

  public isVisible(button: IButtonInfo) {
    return button.isVisible && button.isVisible();
  }

  private isSaveSnapshotDisabled = (): boolean => !this.snapshotService.canSaveSnapshot(this.deviceId) || this.isFetching || !this.isSaveSnapshotEnabled;

  private canCheckin(): boolean {
    // use cached value if api call already executed
    return this.deviceCanCheckin;
  }

  // Avoid exceeding api rate limit exception
  private resetRefresh() {
    this.setRefreshButton(true)
    setTimeout(() => {
      this.setRefreshButton(false);
    }, 5000);
  }

  private setRefreshButton(isRefreshing: boolean): void {
    this.refreshing = isRefreshing;
  }

  private initCheckinStatus() {
    this.deviceCanCheckin = false;
    if (this.device) {
      this.deviceService
          .getDevicePermissionCompatibilityResource(this.deviceId, 'checkin', this.device.mcConnectionId)
          .pipe(first())
          .subscribe((mcDeviceActionCompatibilityReport: hd.IMcDeviceActionCompatibilityReport) => {
              if (this.device && (this.device.mcDeviceId || this.device.deviceId)) {
                  this.deviceCanCheckin = mcDeviceActionCompatibilityReport.compatibleAccessibleDevices.some(x => x === (this.device.mcDeviceId || this.device.deviceId));
                  this.isAnyButton.emit(this.ButtonList.some((button) => button.isVisible()));
              }
          });
    }
  }
}
